import React from "react";
import {
  CheckCircle2,
  IdCard,
  MapPin,
  Receipt,
  Smartphone,
  Globe,
  AlertCircle,
  ExternalLink,
} from "lucide-react";

/**
 * Instrucciones paso-a-paso específicas para cada método manual
 * (Western Union, MoneyGram, Remitly). Aparece debajo de los datos
 * del beneficiario en /pagar como Paso 1.5 antes del reporte.
 *
 * El contenido es genérico y aplica al destinatario real configurado
 * en plans_config.py (Ricardo Zermeño Mejía, Guadalajara, Jalisco, MX).
 */
const INSTRUCTIONS = {
  western_union: {
    methodName: "Western Union",
    color: "amber",
    accent: "bg-amber-100 text-amber-800",
    headerBg: "bg-amber-50 border-amber-300",
    iconBg: "bg-amber-500",
    badge: "PASO 1.5 · CÓMO ENVIAR POR WESTERN UNION",
    title: "Instrucciones detalladas — Western Union",
    intro:
      "Western Union es la red de remesas más antigua. Tiene sucursales en supermercados (Walmart, Kroger, CVS) y tiendas de envío en todo Estados Unidos.",
    options: [
      {
        Icon: MapPin,
        title: "Opción A · En sucursal física",
        steps: [
          "Busca la sucursal más cercana en westernunion.com/us/en/find-locations.html",
          "Lleva tu identificación con foto vigente (matrícula, pasaporte, ID estatal, licencia)",
          "Lleva el monto exacto en efectivo o tarjeta de débito",
          "Dile al cajero: «Quiero hacer un envío de dinero a México»",
          "Da el nombre completo del destinatario tal como aparece arriba",
          "Indica: Guadalajara, Jalisco, México",
        ],
      },
      {
        Icon: Smartphone,
        title: "Opción B · App o sitio web",
        steps: [
          "Descarga la app «Western Union: Send Money» o entra a westernunion.com",
          "Selecciona país destino: «México»",
          "Modo de entrega: «Recogida en efectivo» (Cash Pickup)",
          "Llena los datos del beneficiario tal como aparecen arriba",
          "Paga con tu tarjeta o cuenta bancaria",
          "Al final recibirás un número MTCN de 10 dígitos",
        ],
      },
    ],
    reference: {
      title: "Lo que vas a guardar para reportar tu pago",
      items: [
        "MTCN — Money Transfer Control Number (10 dígitos)",
        "Recibo impreso o PDF con la fecha del envío",
        "Foto del comprobante (por si lo pierdes)",
      ],
    },
    cost: "Costo del envío: aproximadamente $5 a $12 USD adicionales según método de pago. Lo paga quien envía (no se descuenta del plan).",
    web: "westernunion.com",
  },
  money_gram: {
    methodName: "MoneyGram",
    color: "rose",
    accent: "bg-rose-100 text-rose-800",
    headerBg: "bg-rose-50 border-rose-300",
    iconBg: "bg-rose-600",
    badge: "PASO 1.5 · CÓMO ENVIAR POR MONEYGRAM",
    title: "Instrucciones detalladas — MoneyGram",
    intro:
      "MoneyGram tiene presencia en Walmart, CVS, 7-Eleven, Albertsons y miles de tiendas en EE.UU. Las comisiones suelen ser un poco más bajas que Western Union.",
    options: [
      {
        Icon: MapPin,
        title: "Opción A · En sucursal física",
        steps: [
          "Busca el agente MoneyGram más cercano en moneygram.com/mgo/us/en/find-a-location",
          "Lleva ID con foto (matrícula, pasaporte, ID estatal)",
          "Lleva el monto exacto en efectivo o tarjeta",
          "Pide hacer un envío a México — modalidad «10 minutos» o «al día siguiente»",
          "Llena el formulario amarillo con los datos del destinatario",
          "Indica: Guadalajara, Jalisco, México",
        ],
      },
      {
        Icon: Smartphone,
        title: "Opción B · App MoneyGram",
        steps: [
          "Descarga la app oficial «MoneyGram Money Transfer» de la App Store o Play Store",
          "Crea cuenta con tu correo electrónico",
          "Selecciona destino: México — Cash Pickup",
          "Llena los datos del beneficiario tal como aparecen arriba",
          "Paga con tarjeta de débito, crédito o cuenta bancaria",
          "Recibirás un número de referencia de 8 dígitos",
        ],
      },
    ],
    reference: {
      title: "Lo que vas a guardar para reportar tu pago",
      items: [
        "Número de referencia (8 dígitos)",
        "Recibo impreso o captura de pantalla con la confirmación",
        "Fecha y sucursal donde hiciste el envío",
      ],
    },
    cost: "Costo del envío: aproximadamente $4 a $10 USD según método de pago y velocidad de entrega.",
    web: "moneygram.com",
  },
  remitly: {
    methodName: "Remitly",
    color: "emerald",
    accent: "bg-emerald-100 text-emerald-800",
    headerBg: "bg-emerald-50 border-emerald-300",
    iconBg: "bg-emerald-600",
    badge: "PASO 1.5 · CÓMO ENVIAR POR REMITLY",
    title: "Instrucciones detalladas — Remitly",
    intro:
      "Remitly es 100% digital. Solo necesitas tu celular y una tarjeta o cuenta bancaria. No hace falta ir a ninguna sucursal. Es el más rápido y económico de los tres.",
    options: [
      {
        Icon: Smartphone,
        title: "Opción A · App Remitly (recomendado)",
        steps: [
          "Descarga la app «Remitly: Send Money Abroad» de la App Store o Play Store",
          "Regístrate con tu correo electrónico y celular de EE.UU.",
          "Verifica tu identidad subiendo foto de tu ID (matrícula, pasaporte o licencia)",
          "Crea un envío a México — selecciona «Cash Pickup at Elektra/Banorte/Telecomm»",
          "Llena los datos del beneficiario tal como aparecen arriba",
          "Paga con tarjeta de débito (rápido) o cuenta bancaria (más barato)",
          "Recibirás un código de confirmación inmediato",
        ],
      },
      {
        Icon: Globe,
        title: "Opción B · Sitio web",
        steps: [
          "Entra a remitly.com desde tu computadora o celular",
          "Crea cuenta con correo electrónico",
          "Sigue los mismos pasos que en la app",
        ],
      },
    ],
    reference: {
      title: "Lo que vas a guardar para reportar tu pago",
      items: [
        "ID de transacción de Remitly (16 caracteres alfanuméricos)",
        "Captura de pantalla de la confirmación dentro de la app",
        "Correo electrónico que envía Remitly con los datos",
      ],
    },
    cost: "Primer envío suele ser gratis. Envíos siguientes: $0.99 a $3.99 USD según tu plan elegido (Economy / Express).",
    web: "remitly.com",
  },
};

export default function MethodInstructions({ method }) {
  const d = INSTRUCTIONS[method];
  if (!d) return null;

  return (
    <div
      data-testid={`method-instructions-${method}`}
      className={`border-2 rounded-2xl p-5 sm:p-6 ${d.headerBg}`}
    >
      <div className="flex items-start gap-3 mb-4">
        <div
          className={`flex-shrink-0 w-11 h-11 rounded-xl ${d.iconBg} text-white flex items-center justify-center`}
        >
          <Receipt className="w-5 h-5" strokeWidth={2.5} />
        </div>
        <div className="min-w-0">
          <p className="text-[10px] font-black uppercase tracking-widest text-zinc-700 mb-1">
            {d.badge}
          </p>
          <h3 className="text-base sm:text-lg font-black text-zinc-900 leading-tight">
            {d.title}
          </h3>
        </div>
      </div>

      <p className="text-xs sm:text-sm text-zinc-700 leading-snug mb-4">{d.intro}</p>

      <div className="space-y-4">
        {d.options.map((opt) => (
          <div
            key={opt.title}
            className="bg-white border border-zinc-200 rounded-xl p-4"
          >
            <div className="flex items-center gap-2 mb-2.5">
              <opt.Icon className={`w-4 h-4 text-zinc-700`} />
              <p className="text-sm font-black text-zinc-900">{opt.title}</p>
            </div>
            <ol className="space-y-1.5">
              {opt.steps.map((s, idx) => (
                <li key={`${opt.title}-${idx}`} className="flex items-start gap-2 text-xs text-zinc-700">
                  <span
                    className={`flex-shrink-0 w-5 h-5 rounded-full ${d.accent} font-black text-[10px] flex items-center justify-center mt-0.5`}
                  >
                    {idx + 1}
                  </span>
                  <span className="leading-snug">{s}</span>
                </li>
              ))}
            </ol>
          </div>
        ))}
      </div>

      <div className="mt-4 bg-white border border-zinc-200 rounded-xl p-4">
        <div className="flex items-center gap-2 mb-2">
          <Receipt className="w-4 h-4 text-zinc-700" />
          <p className="text-sm font-black text-zinc-900">{d.reference.title}</p>
        </div>
        <ul className="space-y-1">
          {d.reference.items.map((item) => (
            <li key={item} className="flex items-start gap-2 text-xs text-zinc-700">
              <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600 flex-shrink-0 mt-0.5" />
              <span className="leading-snug">{item}</span>
            </li>
          ))}
        </ul>
      </div>

      <div className="mt-3 grid sm:grid-cols-2 gap-3">
        <div className="bg-white border border-zinc-200 rounded-xl p-3">
          <p className="text-[10px] font-black uppercase tracking-widest text-zinc-500 mb-1 flex items-center gap-1">
            <AlertCircle className="w-3 h-3" /> Costo aproximado
          </p>
          <p className="text-xs text-zinc-700 leading-snug">{d.cost}</p>
        </div>
        <a
          href={`https://${d.web}`}
          target="_blank"
          rel="noopener noreferrer"
          className="bg-white border border-zinc-200 rounded-xl p-3 hover:bg-zinc-50 transition-colors"
          data-testid={`method-instructions-${method}-web`}
        >
          <p className="text-[10px] font-black uppercase tracking-widest text-zinc-500 mb-1 flex items-center gap-1">
            <Globe className="w-3 h-3" /> Sitio web oficial
          </p>
          <p className="text-xs font-bold text-blue-700 flex items-center gap-1">
            {d.web} <ExternalLink className="w-3 h-3" />
          </p>
        </a>
      </div>

      <div className="mt-4 flex items-start gap-2 bg-amber-50 border border-amber-300 rounded-lg p-3">
        <IdCard className="w-4 h-4 text-amber-700 flex-shrink-0 mt-0.5" />
        <p className="text-[11px] text-amber-900 leading-snug">
          <span className="font-black">Importante:</span> el nombre que des al enviar el dinero
          debe coincidir con el que aparece en la lista del beneficiario arriba. Si te equivocas
          aunque sea una letra, el destinatario no podrá recoger el envío.
        </p>
      </div>
    </div>
  );
}
